import { stripKnowledgeContent } from '@/components/knowledge/KnowledgeArticleContent'
import type { ProjectRow, SupportKnowledgeArticleRow, SupportKnowledgeMaterialType } from '@/types/api'

export type ProjectArticlesBundle = {
  project: ProjectRow
  articles: SupportKnowledgeArticleRow[]
  loadError: string | null
}

/** Виртуальная вкладка «Общие» — материалы без привязки к проекту (project_id IS NULL). */
export const KNOWLEDGE_GENERAL_TAB_ID = '__general__'

export function isKnowledgeGeneralTab(projectId: string | null | undefined): boolean {
  return projectId === KNOWLEDGE_GENERAL_TAB_ID
}

export function generalArticlesFromList(articles: SupportKnowledgeArticleRow[]): SupportKnowledgeArticleRow[] {
  return articles.filter((a) => !a.project_id)
}

export const MATERIAL_TYPE_COLORS: Record<string, string> = {
  product_doc: 'blue',
  response_template: 'teal',
  macro: 'violet',
  script: 'orange',
  faq: 'cyan',
  guide: 'indigo',
  instruction: 'indigo',
  other: 'gray',
}

export const MATERIAL_TYPE_LABELS: Record<string, string> = {
  product_doc: 'Продуктовый документ',
  response_template: 'Шаблон ответа',
  macro: 'Макрос',
  script: 'Скрипт оператора',
  faq: 'FAQ',
  guide: 'Инструкция',
  instruction: 'Инструкция',
  other: 'Прочее',
}

export const FALLBACK_MATERIAL_TYPES: Array<{ key: SupportKnowledgeMaterialType; label: string }> = [
  { key: 'product_doc', label: MATERIAL_TYPE_LABELS.product_doc },
  { key: 'response_template', label: MATERIAL_TYPE_LABELS.response_template },
  { key: 'macro', label: MATERIAL_TYPE_LABELS.macro },
  { key: 'script', label: MATERIAL_TYPE_LABELS.script },
  { key: 'faq', label: MATERIAL_TYPE_LABELS.faq },
  { key: 'instruction', label: MATERIAL_TYPE_LABELS.instruction },
  { key: 'other', label: MATERIAL_TYPE_LABELS.other },
]

export function articleCountLabel(n: number): string {
  const m = n % 10
  const k = n % 100
  if (m === 1 && k !== 11) return `${n} материал`
  if (m >= 2 && m <= 4 && (k < 12 || k > 14)) return `${n} материала`
  return `${n} материалов`
}

export function articlePreviewText(article: SupportKnowledgeArticleRow): string {
  if (article.content_preview) return article.content_preview
  return stripKnowledgeContent(article.content ?? '', article.content_format ?? 'html')
}

function looksLikeKnowledgeHtml(content: string, contentFormat?: string | null): boolean {
  return contentFormat === 'html' || /<\s*(p|br|ul|ol|li|h[1-4]|img|strong|em|blockquote|pre|div)\b/i.test(content)
}

const BASE_READY_REPLY_HEADINGS = [
  'готовый текст',
  'готовый ответ',
  'готовый ответ клиенту',
  'ответ клиенту',
  'текст ответа',
]

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeSectionTitle(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.:;—-]+$/g, '')
    .trim()
    .toLowerCase()
}

function readyReplyHeadingLabels(article: SupportKnowledgeArticleRow): string[] {
  const labels = [...BASE_READY_REPLY_HEADINGS]
  if (article.material_type === 'faq') labels.push('ответ')
  return labels
}

function readyBlockFromPlainText(plain: string, labels: string[]): string {
  const escapedLabels = labels.map(escapeRegExp).join('|')
  const match = plain.match(
    new RegExp(`(?:^|\\n)##\\s*(?:${escapedLabels})\\s*[.:;—-]?\\s*\\n([\\s\\S]*?)(?=\\n##\\s|$)`, 'i'),
  )
  return (match?.[1] || '').trim()
}

function normalizeReadyReplyPlainText(value: string): string {
  return value
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function inlineNodeText(node: Node, listDepth = 0): string {
  if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || ''
  if (node.nodeType !== Node.ELEMENT_NODE) return ''

  const element = node as Element
  const tag = element.tagName.toLowerCase()
  if (tag === 'br') return '\n'
  if (tag === 'img') return element.getAttribute('alt')?.trim() || ''
  if (tag === 'ul' || tag === 'ol') return `\n${listNodeText(element, listDepth)}`
  return Array.from(element.childNodes)
    .map((child) => inlineNodeText(child, listDepth))
    .join('')
}

function listNodeText(list: Element, depth = 0): string {
  const ordered = list.tagName.toLowerCase() === 'ol'
  const items = Array.from(list.children).filter((child) => child.tagName.toLowerCase() === 'li')
  return items
    .map((item, index) => {
      const nestedLists = Array.from(item.children).filter((child) => {
        const tag = child.tagName.toLowerCase()
        return tag === 'ul' || tag === 'ol'
      })
      const nestedSet = new Set(nestedLists)
      const itemText = Array.from(item.childNodes)
        .filter((child) => !(child.nodeType === Node.ELEMENT_NODE && nestedSet.has(child as Element)))
        .map((child) => inlineNodeText(child, depth + 1))
        .join('')
        .replace(/[ \t]*\n[ \t]*/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .trim()
      const marker = ordered ? `${index + 1}.` : '•'
      const indent = '  '.repeat(depth)
      const nested = nestedLists
        .map((child) => listNodeText(child, depth + 1))
        .filter(Boolean)
        .join('\n')
      return `${indent}${marker} ${itemText}${nested ? `\n${nested}` : ''}`.trimEnd()
    })
    .join('\n')
}

function blockNodeText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.nodeValue || '').trim()
  if (node.nodeType !== Node.ELEMENT_NODE) return ''

  const element = node as Element
  const tag = element.tagName.toLowerCase()
  if (tag === 'ul' || tag === 'ol') return listNodeText(element)

  const text = inlineNodeText(element)
  if (tag === 'blockquote') {
    return text
      .split('\n')
      .map((line) => (line.trim() ? `> ${line.trim()}` : '>'))
      .join('\n')
  }
  return text.trim()
}

function readyBlockFromHtml(html: string, labels: string[]): string {
  if (typeof DOMParser === 'undefined') return ''
  try {
    const allowedLabels = new Set(labels.map(normalizeSectionTitle))
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const headings = Array.from(doc.querySelectorAll('h1,h2,h3,h4'))
    const heading = headings.find((node) => allowedLabels.has(normalizeSectionTitle(node.textContent || '')))
    if (!heading) return ''

    const parts: string[] = []
    let node = heading.nextSibling
    while (node) {
      if (node.nodeType === Node.ELEMENT_NODE && /^H[1-4]$/i.test((node as Element).tagName)) break
      const text = blockNodeText(node)
      if (text) parts.push(text)
      node = node.nextSibling
    }
    return normalizeReadyReplyPlainText(parts.join('\n\n'))
  } catch {
    return ''
  }
}

export function articleReadyReplyText(article: SupportKnowledgeArticleRow): string {
  const raw = article.content || ''
  if (!raw.trim()) return ''
  const labels = readyReplyHeadingLabels(article)
  if (looksLikeKnowledgeHtml(raw, article.content_format)) {
    return (
      readyBlockFromHtml(raw, labels) ||
      readyBlockFromPlainText(stripKnowledgeContent(raw, article.content_format ?? 'html'), labels)
    )
  }
  return readyBlockFromPlainText(raw, labels)
}

export function articleCopyText(article: SupportKnowledgeArticleRow): string {
  const plain = stripKnowledgeContent(article.content ?? '', article.content_format ?? 'html')
  if (!plain) return ''
  if (!['macro', 'response_template', 'faq'].includes(article.material_type || '')) return plain

  return articleReadyReplyText(article) || plain
}

export function authorLabel(row: SupportKnowledgeArticleRow): string {
  const u = row.updated_by || row.created_by
  if (!u) return '—'
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
  return name || u.email || '—'
}

export function groupArticlesByProject(
  projects: ProjectRow[],
  articles: SupportKnowledgeArticleRow[],
): ProjectArticlesBundle[] {
  const byProject = new Map<string, SupportKnowledgeArticleRow[]>()
  for (const article of articles) {
    if (!article.project_id) continue
    const list = byProject.get(article.project_id) ?? []
    list.push(article)
    byProject.set(article.project_id, list)
  }
  return projects.map((project) => ({
    project,
    articles: byProject.get(project.id) ?? [],
    loadError: null,
  }))
}

export function countByType(articles: SupportKnowledgeArticleRow[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const a of articles) {
    const key = a.material_type || 'other'
    m.set(key, (m.get(key) ?? 0) + 1)
  }
  return m
}
